import cn from 'classnames'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Option } from '../..'
import styles from './ComboBoxValue.module.scss'
import { Chip } from '@/components/Chip'

interface ComboBoxValueProps<TValue> {
  className?: string
  selectedOptions: Option<TValue>[]
  onDelete: (value: TValue) => void
}
export const ComboBoxValue = <TValue,>({
  className,
  selectedOptions,
  onDelete,
}: ComboBoxValueProps<TValue>) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [visibleOptions, setVisibleOptions] = useState<Option<TValue>[]>([])

  const recalcVisibleOptions = useCallback(
    (containerWidth: number, selectedOptions: Option<TValue>[]) => {
      const options = calculateVisibleItems(containerWidth, selectedOptions)
      setVisibleOptions(options)
    },
    []
  )

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      const { width } = entries[0].contentRect
      recalcVisibleOptions(width, selectedOptions)
    })

    resizeObserver.observe(containerRef.current)
  }, [recalcVisibleOptions, selectedOptions])

  return (
    <div className={cn(styles.container, className)} ref={containerRef}>
      {visibleOptions.map((option) => (
        <div key={String(option.value)}>
          <SelectedOption option={option} onDelete={onDelete} />
        </div>
      ))}

      {visibleOptions.length < selectedOptions.length ? (
        <Chip
          className={styles.chip}
          title={`+${selectedOptions.length - visibleOptions.length}`}
          variant="activated"
        />
      ) : null}
    </div>
  )
}

interface SelectedOptionProps<TValue> {
  option: Option<TValue>
  onDelete: (value: TValue) => void
}
const SelectedOption = <TValue,>({
  option,
  onDelete,
}: SelectedOptionProps<TValue>) => {
  const click = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      e.nativeEvent.stopImmediatePropagation()
      onDelete(option.value)
    },
    [onDelete, option.value]
  )

  return (
    <Chip
      className={styles.chip}
      title={option.label}
      variant="activated"
      onClick={click}
      isDeletable
    />
  )
}

function calculateVisibleItems<TValue>(
  containerWidth: number,
  selectedOptions: Option<TValue>[]
): Option<TValue>[] {
  const elementWidthOffset = 42
  const charWidth = 9

  let currentWidth = 0

  const visibleOptions = []

  for (const option of selectedOptions) {
    const optionWidth = option.label.length * charWidth + elementWidthOffset
    if (currentWidth + optionWidth <= containerWidth) {
      visibleOptions.push(option)
      currentWidth += optionWidth
    } else {
      break
    }
  }

  return visibleOptions
}
